// The route-independent composition root (design DD1/DD2/DD3/DD4/DD5): `App` renders
// `BrowserRouter → SessionController → AppRoutes`, and `SessionController` is the one
// place that owns the match `Connection` for its whole lifetime and injects the live
// route seams into `AppRoutes` — closing D-071, the gap where `main.tsx` rendered a
// bare `<App/>` with every seam `undefined` so no lobby join ever turned into a live
// match.
//
// Group 2 landed the ownership + injection core (one `Connection` via the registry,
// the wired factory callbacks, the session-scoped `LodController`, and the derived
// `colonyController`/`inMatchDeps`/`lod`/`createRenderer`). Group 3 adds the
// **session-phase machine** (`lobby|connecting|in-match|finished`), the **root-owned
// `LobbyClient` + roster poll loop**, and a **pinned, bounded, host-aware
// `connecting`** bridge (design DD5, W6-F1): the Lobby CTA hand-off (`create`/`join`/
// `claim`/`ready`/`start`/`cancel`/`retry`) runs entirely through the root, and
// `Lobby.tsx` is now a presentational screen that constructs no `LobbyClient` and no
// `Connection` of its own. Spectator/role reconciliation (group 4), reload persistence
// (group 5), reset + completeness (group 6), and StrictMode idempotence (group 7)
// build on this seam without changing the component's external shape.

import { useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { CanvasRenderer } from '../canvas/renderer';
import type { Camera } from '../canvas/projection';
import {
  ConnState,
  type ConnectionConfig,
  type Rng,
  type Scheduler,
  type TransportFactory,
} from '../net/wire/connection';
import { LobbyClient, LobbyHttpError, type LobbyConfig } from '../net/lobby-http';
import { LodController, type LodControllerConfig } from '../net/wire/lod';
import { PROTOCOL_VERSION, Tier, type ViewportRect } from '../net/wire/messages';
import {
  clearMatchSession,
  getMatchSession,
  getOrCreateMatchSession,
  type MatchSession,
  type MatchSessionConfig,
  type MatchSessionKey,
} from '../session/match-session';
import { bindColonyMessages } from '../ui/colony/bind-messages';
import { bindColonyRejection, createColonyController, type ColonyController } from '../ui/colony/controller';
import type { MatchCameraLodSeam, MatchHudSeams, MatchLodSeam } from '../ui/screens/Match';
import type { ColonyLodSeam, ColonyUnderlaySeams } from '../ui/screens/Colony';
import type { SoundControlSeams } from '../ui/components/hud/SoundControl';
import { selectOwnEliminated, useClientStore } from '../state/store';
import { defaultTierForZoom } from '../ui/camera/camera-gestures';
import { AppRoutes } from './App';
import { usePvpChatSeam } from './pvp-chat';
import { PvpPromptFeed } from './PvpPromptFeed';
import type { InMatchCanvasDeps, InMatchRenderer } from './InMatchCanvas';
import type { ChatSeam } from '../ui/colony/chat-seam';

/** The client-phase machine `SessionController` drives (design DD5): `lobby` (no
 * socket open), `connecting` (a bounded, cancellable hand-off attempt), `in-match`
 * (a live session), `finished` (the match ended, `Summary` reachable). */
export type SessionPhase = 'lobby' | 'connecting' | 'in-match' | 'finished';

/** The persisted record `SessionStore` carries across a page reload (design DD8):
 * exactly the five fields the reload-classification table (D9) needs — no route,
 * no connection state. */
export interface PersistedSession {
  token: string;
  matchId: string;
  role: 'player' | 'spectator' | null;
  phase: SessionPhase;
  isHost: boolean;
}

/** The persisted-session seam (design DD8): `get`/`set`/`clear` over one record.
 * Production supplies a thin adapter over `window.sessionStorage` (group 7); tests
 * inject `test/fake-session-store.ts`'s in-memory double so reload scenarios never
 * touch real browser storage. */
export interface SessionStore {
  get(): PersistedSession | null;
  set(record: PersistedSession): void;
  clear(): void;
}

/** The lobby HTTP client's injectable half (mirrors `LobbyClientDeps`, minus the
 * root's own default `baseUrl`). */
export interface SessionControllerLobbyDeps {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  /** Reserves colony 1 for another human instead of spawning AutoBee. Presenter playoff only. */
  opponent?: 'auto' | 'human';
}

/**
 * The presentational Lobby's hand-off seam (design DD5). The composition root owns
 * the one `LobbyClient` and the roster poll loop; `Lobby.tsx` reads the roster from
 * the store and calls these entry points, constructing no `LobbyClient` and no
 * `Connection` of its own. `phase`/`connectingStalled` are the reactive bits the
 * screen renders its connecting surface from (the phase itself is root React state,
 * not a `SessionSlice` field — W3-F4).
 */
export interface SessionLobbyController {
  /** The current session phase (the Lobby renders its connecting surface from it). */
  phase: SessionPhase;
  /** True once a **host** connecting attempt has exhausted its budget (W6-F1): the
   * Lobby surfaces a `retry()` entry point instead of auto-dropping. */
  connectingStalled: boolean;
  /** Host: create a new match (does not open a connection — the hand-off waits for
   * `start()`). */
  create(name: string, config: LobbyConfig): Promise<void>;
  /** Non-host: join an existing match by code (does not open a connection). */
  join(matchCode: string, name: string): Promise<void>;
  /** Claim/rename the own colony in the lobby roster. */
  claim(colonyId: number): Promise<void>;
  /** Toggle readiness; `ready(true)` (the non-host "Join the meadow" CTA) also enters
   * the bounded `connecting` state. */
  ready(ready: boolean): Promise<void>;
  /** Host: lock config, advance the match `Lobby → Starting`, and enter `connecting`. */
  start(): Promise<void>;
  /** Cancel a non-host connecting attempt: disconnect, un-ready (`ready(false)`), and
   * return to the lobby. A no-op outside `connecting`. */
  cancel(): Promise<void>;
  /** Re-arm the budget and re-open the connection for a stalled host attempt (W6-F1). */
  retry(): void;
}

/**
 * The composition root's full injected-seam bundle (design DD1). Production
 * (`main.tsx`, group 7) supplies the real WebSocket `transportFactory`, the
 * same-origin, root-relative lobby (`DEFAULT_LOBBY_BASE_URL`, matching
 * `waggle-server`'s root-mounted `/matches` routes), `window.sessionStorage`, a
 * wall-clock scheduler/RNG, and the PixiJS `createRenderer` — every default mirrors what
 * `InMatchCanvas` defaulted before this seam existed. Tests supply the mock transport
 * (`test/mock-transport.ts`), a scripted `fetchImpl`, the fake `SessionStore`
 * (`test/fake-session-store.ts`), and fake scheduler/RNG/renderer doubles so the
 * whole root is mountable server-free.
 */
export interface SessionControllerDeps {
  transportFactory: TransportFactory;
  lobby?: SessionControllerLobbyDeps;
  sessionStore?: SessionStore;
  scheduler?: Scheduler;
  rng?: Rng;
  createRenderer?: (container: HTMLElement, tickRateHz: number) => InMatchRenderer;
  /** Pinned bounded-`connecting` budget (design DD5), default 90 000 ms. */
  connectingBudgetMs?: number;
  /** Presentation rendezvous may intentionally remain pre-Welcome until the presenter starts. */
  handshakeTimeoutMs?: number;
  /** Root-owned lobby roster poll interval (design DD5), default 2 000 ms. */
  pollIntervalMs?: number;
  /**
   * The in-match `SoundControl` seam bundle (client-audio DD1/DD6), forwarded to `AppRoutes` so
   * the Colony + Match HUDs render the sound toggle wired to the shared lifecycle + engine. Not a
   * DI singleton like the transport/scheduler — it is supplied at runtime by the sibling
   * app-root `initAudio` mount (`App.tsx`), undefined until the audio controller arms and in
   * every existing test that constructs `SessionController` without audio (additive-only).
   */
  sound?: SoundControlSeams;
  /**
   * **The pvp prototype's discovery flag** (`docs/dev/pvp-prototype.md`), resolved once at boot on
   * the mode probe the client already makes (`bootstrap.tsx` → `resolveBootSurface`) and handed in
   * rather than probed for here — the live session path adds no request of its own. Absent/false in
   * every shipped deployment and in every test that does not ask for it, where the `/colony` route
   * gets no chat seam and `Colony` renders exactly as it always has.
   */
  pvp?: boolean;
  /**
   * **An explicit override for the two-colony Match HUD's reads** (`pvp-ui-fixes` W3).
   *
   * Absent — every player path — the seams are derived from the live session below exactly as
   * before, so this changes nothing for anyone who does not pass it. The presentation projector is
   * the one caller that cannot be served by that derivation: it reads *both* colonies' prompts
   * with a presentation capability the live session knows nothing about, while reading open board
   * state with its own spectator session. Two capabilities, one screen, so the composition that
   * holds them supplies them rather than the session guessing.
   */
  matchHud?: MatchHudSeams;
  /** Presentation players leave the event match for the deployment's ranked Rounds home. */
  presentationRoundsHandoff?: boolean;
  /** Presenter Preview keeps the real Player View interactive while intercepting every commit. */
  readOnlyPreview?: {
    onAttempt(): void;
    chat: { presenterToken: string; team: 'red' | 'blue' };
  };
}

// --- production defaults (each mirrors the pre-D-071 `InMatchCanvas` default) -----

// The whole-field LOD viewport, CENTRED at the origin (colony-match-nav review finding):
// world space is centred (`canvas/projection.ts`'s `ESTABLISHING_CAMERA`/the lod.rs
// quantization contract maps ±fieldSize/2 to the u16 range), so the establishing detail
// subscription requests that same centred rect. The server now projects the whole world, but
// retaining a truthful viewport keeps camera intent and older peers well-defined.
const DEFAULT_VIEWPORT: ViewportRect = { min: [-2048, -2048], max: [2048, 2048] };
const DEFAULT_CAMERA: Camera = { offsetX: 2048, offsetY: 2048, scale: 0.2 };
const DEFAULT_CONNECTION_CONFIG: Omit<ConnectionConfig, 'hello'> = {
  handshakeTimeoutMs: 5_000,
  firstKeyframeTimeoutMs: 5_000,
  gapRecoveryTimeoutMs: 5_000,
  backoffBaseMs: 250,
  backoffCapMs: 8_000,
};
const DEFAULT_LOD_CONFIG: LodControllerConfig = { coalesceMs: 100, viewportEpsilon: 0.02 };

/**
 * **Which seams the two-colony Match HUD reads through** (`pvp-ui-fixes` W3).
 *
 * Three cases, and the order between the first two is the whole point:
 * 1. seams supplied by the composition win outright — the presentation projector holds two
 *    capabilities (a presentation bearer for authored content, its own spectator session for open
 *    board state) that no derivation from a single live session could reconstruct;
 * 2. otherwise the pvp prototype's player seams are derived from the live session, exactly as
 *    before this function existed;
 * 3. otherwise nothing, and the HUD makes no request at all.
 *
 * Extracted as a pure function rather than left inline because case 2 is the one a careless edit
 * would swallow: `explicit ?? derived` reads the same whether or not the derived branch still
 * works, and no test previously exercised it.
 */
export function resolveMatchHudSeams(input: {
  explicit?: MatchHudSeams;
  pvp?: boolean;
  liveToken: string | null;
  baseUrl?: string;
  fetchImpl?: typeof fetch;
}): MatchHudSeams | undefined {
  if (input.explicit !== undefined) {
    return input.explicit;
  }
  if (input.pvp !== true || input.liveToken === null) {
    return undefined;
  }
  return {
    mode: 'player',
    token: input.liveToken,
    baseUrl: input.baseUrl,
    fetchImpl: input.fetchImpl,
  };
}
/** The same-origin lobby API base: empty, so `${base}${path}` composes to a
 * root-relative path (`/matches`, `/matches/{id}/join`, ...) against the current
 * origin — exactly where `waggle-server` mounts the lobby HTTP routes
 * (`waggle-server/src/router.rs`; `docs/specs/PROTOCOL.md`). **Not** `/api`: nothing
 * is mounted under that prefix in production (`server-static-client` deliberately
 * reserves it so an unmounted request there 404s rather than being answered with the
 * SPA shell), so a non-empty `/api` default here made the packaged client dial a path
 * the server never serves — the finding this constant now closes. Every interpolation
 * site below composes `${baseUrl}${path}` where `path` already starts with `/`, so the
 * empty string never produces a double slash or a bare relative path. */
const DEFAULT_LOBBY_BASE_URL = '';
/** The pinned bounded-`connecting` budget (design DD5): well past normal host-start
 * latency, so a slow start is the tail case, not the common path. */
const DEFAULT_CONNECTING_BUDGET_MS = 90_000;
/** The root-owned roster poll interval (design DD5). */
const DEFAULT_POLL_INTERVAL_MS = 2_000;

function wallClockScheduler(): Scheduler {
  return {
    setTimer: (fn, ms) => window.setTimeout(fn, ms) as unknown as number,
    clearTimer: (id) => window.clearTimeout(id),
  };
}

function mathRandomRng(): Rng {
  return { next: () => Math.random() };
}

function defaultCreateRenderer(container: HTMLElement, tickRateHz: number): InMatchRenderer {
  return new CanvasRenderer(container, tickRateHz);
}

/** The route seams the root derives from the single session (design DD1). */
interface DerivedSeams {
  colonyController: ColonyController;
  inMatchDeps: InMatchCanvasDeps;
  lod: MatchLodSeam;
  /** The Colony route's LOD seam (design DD6, colony-match-nav): the session's OWN
   * `LodController` satisfies `ColonyLodSeam` structurally (`requestTier`/`setZoom`/
   * `setViewport`) — no adapter object needed, exactly like `cameraLod` below. */
  colonyLod: ColonyLodSeam;
  sessionKey: MatchSessionKey;
  createRenderer: (container: HTMLElement, tickRateHz: number) => InMatchRenderer;
  /** The Match route's interactive camera pan/zoom LOD seam (camera-pan-zoom,
   * `client-app` "Composition root injects the Match-route camera pan/zoom seam"):
   * the session's OWN `LodController` satisfies `MatchCameraLodSeam` structurally
   * (`setZoom`/`setViewport`) — no adapter object needed. Previously derived nowhere, so
   * `Match`'s camera control always ran against its inert default live. */
  cameraLod: MatchCameraLodSeam;
}

/**
 * The composition root (design DD1/DD2/DD5). Owns the match `Connection` and the
 * lobby `LobbyClient`/poll loop for their whole lifetime, drives the session-phase
 * machine, and injects the derived seams into `AppRoutes`; no route constructs or
 * tears down a `Connection`, and the presentational Lobby drives the hand-off through
 * the injected `SessionLobbyController`.
 */
export function SessionController(deps: SessionControllerDeps): ReactElement {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const previewAttemptRef = useRef(deps.readOnlyPreview?.onAttempt);
  previewAttemptRef.current = deps.readOnlyPreview?.onAttempt;

  // **The pvp prototype's Beekeeper seam** (`docs/dev/pvp-prototype.md`), probed once from the
  // server and built from the same session token/colony the live WebSocket uses. `undefined` on
  // every deployment that does not advertise pvp, which is every shipped one — see `pvp-chat.ts`.
  const livePvpChat = usePvpChatSeam({
    enabled: deps.pvp ?? false,
    ...(deps.lobby?.baseUrl === undefined ? {} : { baseUrl: deps.lobby.baseUrl }),
    ...(deps.lobby?.fetchImpl === undefined ? {} : { fetchImpl: deps.lobby.fetchImpl }),
    ...(deps.readOnlyPreview === undefined ? {} : { preview: deps.readOnlyPreview.chat }),
  });
  const pvpChat = useMemo<ChatSeam | undefined>(() => {
    if (deps.readOnlyPreview === undefined) return livePvpChat;
    if (livePvpChat === undefined) return undefined;
    const attempt = () => previewAttemptRef.current?.();
    return {
      getSnapshot: livePvpChat.getSnapshot,
      subscribe: livePvpChat.subscribe,
      sendAndOpen: attempt,
      openExisting: livePvpChat.openExisting,
      stop: attempt,
      // Minimise is a read-only act (it changes nothing but which surface is on screen), so the
      // preview passes it straight through exactly as it does `openExisting`/`close`.
      minimise: livePvpChat.minimise,
      close: livePvpChat.close,
      rehydrate: livePvpChat.rehydrate,
    };
  }, [deps.readOnlyPreview !== undefined, livePvpChat]);

  const [seams, setSeams] = useState<DerivedSeams | null>(null);
  // Reactive phase + host-stalled bits the presentational Lobby renders from. Phase is
  // deliberately root React state (not a `SessionSlice` field, W3-F4); a
  // `sessionPhaseRef` mirror lets the store-subscription nav handler read it
  // synchronously (design DD6).
  const [sessionPhase, setSessionPhaseState] = useState<SessionPhase>('lobby');
  const [connectingStalled, setConnectingStalledState] = useState(false);
  // The forced-refresh notice (design DD11): the `min_version` a v3 server demanded of a
  // stale cached v2 client, or `null` when no upgrade wall is showing. Set by
  // `onUpgradeRequired`, which also drops the session to the lobby phase.
  const [upgradeRequiredMinVersion, setUpgradeRequiredMinVersion] = useState<number | null>(null);

  // The own-hive camera anchor, reactively (design DD7/DD8): drives the Colony-route underlay
  // seam so the injected hive `Camera` tracks the store's `ownHive` — null until the first
  // `Welcome` lands, so the Colony route stays chrome-only until then.
  const ownHive = useClientStore((s) => s.ownHive);
  const liveToken = useClientStore((s) => s.sessionToken);

  // Session machinery that must survive re-renders (a `setState` re-render must not
  // rebuild the connection, the LOD controller, or the lobby client).
  const sessionRef = useRef<MatchSession | null>(null);
  const sessionKeyRef = useRef<MatchSessionKey | null>(null);
  const lodRef = useRef<LodController | null>(null);
  const pendingConfirmTierRef = useRef<Tier | null>(null);

  const sessionPhaseRef = useRef<SessionPhase>('lobby');
  const connectingStalledRef = useRef(false);
  // The attempt kind, recorded when `beginConnecting()` runs so the budget terminal is
  // deterministic (design DD5/DD8, W6-F1) — never derived from a poll.
  const isHostRef = useRef(false);
  const budgetTimerRef = useRef<number | null>(null);
  const pollTimerRef = useRef<number | null>(null);
  // The session credential from create/join (or a page-reload rehydrate, group 5), so
  // `beginConnecting()`/`reconnectOrOpen()` can install the session into the store
  // (which the store-subscription then opens) without the premature open a lobby-only
  // create would otherwise trigger.
  const credentialRef = useRef<{ token: string; matchId: string } | null>(null);
  // Armed by an `in-match` rehydrate (design DD8): a reconnecting reload sets
  // `sessionPhase` straight to the persisted phase and never crosses `connecting`, so
  // DD6's `connecting`-gated entry advance cannot fire for it — this is the reload
  // path's own one-shot, consumed by the same nav subscription on the first
  // `Welcome`+keyframe (`manifest` landing) after rehydrate. A subsequent transient
  // reconnect must not re-arm it (W3-F1 non-interference, W4-F1).
  const reloadEntryPendingRef = useRef(false);
  // Set immediately before a root-initiated `connection.disconnect()` call (budget
  // exhaustion, cancel) so the `onStateChange` handler can tell that apart from the
  // connection closing *itself* — the only other source of `ConnState.Closed` is
  // `Connection`'s own `Rejected{unauthorized}` handling, i.e. a stale token the
  // server no longer honours (design DD8). Consumed synchronously within the same
  // `disconnect()` call, before any other event could reach it.
  const expectingCloseRef = useRef(false);
  // DD9 — StrictMode idempotence, the "did I already start this attempt" ref half:
  // `<StrictMode>` double-invokes the mount effect (mount → cleanup → mount)
  // synchronously as part of the initial commit, before any user interaction is
  // possible. `rehydrateFromStore()` is the only mount-effect call that can reach a
  // live `connection.connect()` synchronously (via `reconnectOrOpen()`'s `existing
  // !== null` branch, once the first invocation has already built the session) — so
  // gating the whole rehydrate on this ref (set once, first invocation wins) is what
  // stops the second, transient invocation from re-issuing `connect()` and churning
  // the epoch. Component-instance-scoped by design: a genuine full page reload mints
  // a fresh module/component instance with a fresh ref, so real rehydration is
  // unaffected — only the synthetic same-instance double-invoke is suppressed.
  const rehydratedRef = useRef(false);

  const schedulerRef = useRef<Scheduler | null>(null);
  const rngRef = useRef<Rng | null>(null);
  const createRendererRef = useRef<((container: HTMLElement, tickRateHz: number) => InMatchRenderer) | null>(null);
  const lobbyClientRef = useRef<LobbyClient | null>(null);
  const onStoreChangeRef = useRef<(state: ReturnType<typeof useClientStore.getState>, prev: ReturnType<typeof useClientStore.getState>) => void>(
    () => undefined,
  );

  // Stable DI singletons, initialised once (guarded) — reading `deps.scheduler`
  // etc. on every render would otherwise mint a fresh scheduler each call and orphan
  // armed timers.
  if (schedulerRef.current === null) schedulerRef.current = deps.scheduler ?? wallClockScheduler();
  if (rngRef.current === null) rngRef.current = deps.rng ?? mathRandomRng();
  if (createRendererRef.current === null) createRendererRef.current = deps.createRenderer ?? defaultCreateRenderer;
  if (lobbyClientRef.current === null) {
    lobbyClientRef.current = new LobbyClient({
      baseUrl: deps.lobby?.baseUrl ?? DEFAULT_LOBBY_BASE_URL,
      fetchImpl: deps.lobby?.fetchImpl,
    });
  }

  const connectingBudgetMs = deps.connectingBudgetMs ?? DEFAULT_CONNECTING_BUDGET_MS;
  const pollIntervalMs = deps.pollIntervalMs ?? DEFAULT_POLL_INTERVAL_MS;

  // --- phase / stalled setters that keep the refs in sync ----------------------

  function setPhase(next: SessionPhase): void {
    sessionPhaseRef.current = next;
    setSessionPhaseState(next);
    if (next === 'in-match' || next === 'finished') {
      clearBudget();
      stopPolling();
    } else {
      schedulePoll();
    }
    persistSession();
  }

  function setStalled(next: boolean): void {
    connectingStalledRef.current = next;
    setConnectingStalledState(next);
  }

  // --- the persisted-session writer (design DD8) --------------------------------

  /** Writes `{token,matchId,role,phase,isHost}` to the injected `SessionStore`: the
   * writer reacts to a `sessionPhase` transition (called from `setPhase`, above) and
   * to a store session-field change (called from the store-subscription handler,
   * below) — `sessionPhase` is root React state, not a `SessionSlice` field, so
   * neither trigger alone would see both kinds of change. A null `credentialRef` (no
   * create/join/rehydrate has run yet, or the session was just torn down/rejected)
   * actively clears the persisted record rather than leaving a stale one behind. */
  function persistSession(): void {
    const sessionStore = deps.sessionStore;
    if (sessionStore === undefined) {
      return;
    }
    const cred = credentialRef.current;
    if (cred === null) {
      sessionStore.clear();
      return;
    }
    sessionStore.set({
      token: cred.token,
      matchId: cred.matchId,
      role: useClientStore.getState().role,
      phase: sessionPhaseRef.current,
      isHost: isHostRef.current,
    });
  }

  // --- the one session-scoped Connection (design DD2) --------------------------

  function startSessionIfReady(): void {
    if (sessionRef.current !== null) {
      return; // a session already exists — construct exactly once (DD2/DD9)
    }
    const { sessionToken, matchId } = useClientStore.getState();
    if (sessionToken === null || matchId === null) {
      return; // no session credential in the store yet — nothing to open
    }
    const scheduler = schedulerRef.current!;
    const rng = rngRef.current!;
    const createRenderer = createRendererRef.current!;
    const key: MatchSessionKey = { sessionToken, matchId };
    sessionKeyRef.current = key;

    const lod = new LodController(
      // The rounds-style live projection is whole-world detail. The connection can therefore
      // establish directly in Close and let the renderer's symbol mode handle overview zooms;
      // viewport changes remain useful camera intent but no longer remove entities.
      { viewport: DEFAULT_VIEWPORT, tier: Tier.Close },
      DEFAULT_LOD_CONFIG,
      { scheduler, tierForZoom: defaultTierForZoom },
      { onSubscribe: () => sessionRef.current?.connection.subscriptionChanged() },
    );
    lodRef.current = lod;

    const store = useClientStore.getState();

    // DD9 — StrictMode idempotence, the "registry check" half: a session already
    // registered for this key (built by an earlier mount of this same key — e.g. a
    // full remount that reuses the still-registered prior connection) is already
    // connecting/connected. Only the call that actually creates the registry entry
    // should issue `connect()`; a call that merely finds the existing entry must not
    // re-issue it and churn the epoch.
    const alreadyRegistered = getMatchSession(key) !== undefined;

    const session = getOrCreateMatchSession(key, (): MatchSessionConfig => ({
      hello: { protocolVersion: PROTOCOL_VERSION, sessionToken, matchId },
      config: {
        ...DEFAULT_CONNECTION_CONFIG,
        handshakeTimeoutMs: deps.handshakeTimeoutMs ?? DEFAULT_CONNECTION_CONFIG.handshakeTimeoutMs,
      },
      deps: {
        transportFactory: deps.transportFactory,
        scheduler,
        rng,
        subscription: () => lod.requested(),
        keyframeDisposition: (tier) => {
          const disposition = lod.keyframeDisposition(tier);
          if (disposition === 'confirm') {
            pendingConfirmTierRef.current = tier;
          }
          return disposition;
        },
      },
      callbacks: {
        onServerMessage: bindColonyMessages(store),
        onMutationRejected: bindColonyRejection(store),
        onStateChange: (state) => {
          store.setConnectionState(state);
          // The live production writer for `connectionEpoch` (design DD11): a real
          // reconnect bumps `Connection._epoch`, so this keeps the store's epoch
          // current for the timeline-completeness invalidation (group 6).
          const current = sessionRef.current;
          if (current !== null) {
            store.setConnectionEpoch(current.connection.epoch);
          }
          // A `Closed` state is either a root-initiated `disconnect()` (budget
          // exhaustion, cancel — already handled by its own caller, flagged via
          // `expectingCloseRef`) or `Connection`'s own `Rejected{unauthorized}`
          // handling: a stale token the server no longer honours (design DD8).
          if (state === ConnState.Closed) {
            if (expectingCloseRef.current) {
              expectingCloseRef.current = false;
            } else {
              handleStaleToken();
            }
          }
        },
        onWorld: (world) => {
          // Publish the world and its sequence atomically (one store write), so the
          // render-loop subscription redraws exactly once per world update and no
          // reconnect keyframe is suppressed by a separately-published `lastSeq`
          // (implementation-review F1).
          store.setWorldFrame(world);
          if (pendingConfirmTierRef.current !== null) {
            lod.confirmKeyframe(pendingConfirmTierRef.current);
            store.setConfirmedTier(pendingConfirmTierRef.current);
            pendingConfirmTierRef.current = null;
          }
        },
        onUpgradeRequired: (minVersion) => {
          // A stale cached v2 client hit a v3 server (design DD11, `colony-field-canvas`):
          // the friends-scale migration policy is a coordinated redeploy with no
          // compatibility window, so the client is terminally rejected — make that
          // recoverable by dropping to the lobby phase and surfacing a visible forced-
          // refresh notice (the upgrade message + a reload affordance), never a silent no-op
          // that strands the client on a blank screen.
          setUpgradeRequiredMinVersion(minVersion);
          setPhase('lobby');
          navigateRef.current('/');
        },
      },
    }));
    sessionRef.current = session;

    const colonyController = deps.readOnlyPreview === undefined
      ? createColonyController(session.connection)
      : { dispatch: () => { deps.readOnlyPreview?.onAttempt(); return 'not-ready' as const; } };
    const lodSeam: MatchLodSeam = { requestTier: (tier) => lod.requestTier(tier) };
    // The Colony route's LOD seam (design DD6, colony-match-nav): the session's OWN
    // `LodController` satisfies `ColonyLodSeam` structurally (`requestTier`/`setZoom`/
    // `setViewport`) — no adapter object needed, exactly like `cameraLod` below. Colony's
    // own mount effect reasserts `Close` via `requestTier` once on route entry, then forwards
    // camera zoom through the now-detail-pinned mapping; both still
    // funnel through the LodController's coalescing debounce, so a mount's `requestTier` +
    // first `setViewport` still emit as one Subscribe.
    const inMatchDeps: InMatchCanvasDeps = { sessionKey: key, createRenderer, camera: DEFAULT_CAMERA };
    setSeams({ colonyController, inMatchDeps, lod: lodSeam, colonyLod: lod, sessionKey: key, createRenderer, cameraLod: lod });

    if (!alreadyRegistered) {
      session.connection.connect();
    }
  }

  /** Opens (first attempt) or re-opens (retry / re-ready) the session's connection.
   * The first attempt installs the credential into the store, which the
   * store-subscription's `startSessionIfReady` then opens; a re-attempt reuses the
   * already-built session and re-`connect()`s it (a new epoch, same token). */
  function reconnectOrOpen(): void {
    const existing = sessionRef.current;
    if (existing !== null) {
      existing.connection.connect();
      return;
    }
    const cred = credentialRef.current;
    if (cred === null) {
      return;
    }
    // A page-reload rehydrate (design DD8) seeds `role` before this runs; preserve it
    // rather than resetting to the fresh-attempt default so the monotonic
    // never-re-promote guard (DD7) has something to read on the very first `Welcome`
    // (W5-F1). A fresh live attempt has no prior role (store role is still `null`),
    // so it still gets the `'player'` default.
    const seededRole = useClientStore.getState().role;
    useClientStore.getState().setSession({
      sessionToken: cred.token,
      matchId: cred.matchId,
      role: seededRole ?? 'player',
    });
    // The store-subscription fires synchronously → `startSessionIfReady` builds and
    // connects the session.
  }

  // --- the bounded connecting budget (design DD5, W6-F1) -----------------------

  function armBudget(): void {
    clearBudget();
    budgetTimerRef.current = schedulerRef.current!.setTimer(() => {
      budgetTimerRef.current = null;
      onBudgetExpired();
    }, connectingBudgetMs);
  }

  function clearBudget(): void {
    if (budgetTimerRef.current !== null) {
      schedulerRef.current!.clearTimer(budgetTimerRef.current);
      budgetTimerRef.current = null;
    }
  }

  function onBudgetExpired(): void {
    if (sessionPhaseRef.current !== 'connecting') {
      return;
    }
    if (isHostRef.current) {
      // Host terminal (W6-F1): stop the auto-retry and disconnect, but never issue the
      // post-`Starting`-invalid `ready(false)` nor drop to a no-longer-`Lobby` match —
      // stay `connecting` and surface the stalled + retry state.
      expectingCloseRef.current = true;
      sessionRef.current?.connection.disconnect();
      setStalled(true);
    } else {
      runNonHostTerminal();
    }
  }

  /** The non-host terminal, shared by a manual `cancel()` and budget exhaustion
   * (design DD5): disconnect, un-ready, and return to the lobby (still polling). The
   * session is kept so a re-ready re-opens the same connection. */
  function runNonHostTerminal(): void {
    clearBudget();
    expectingCloseRef.current = true;
    sessionRef.current?.connection.disconnect();
    const client = lobbyClientRef.current;
    if (client !== null && client.token !== null) {
      void client.ready(false).then(
        (lobby) => useClientStore.getState().setLobby(lobby),
        () => undefined,
      );
    }
    setStalled(false);
    setPhase('lobby');
  }

  // --- the stale-token terminal (design DD8) ------------------------------------

  /** A reconnect whose token the server no longer authorizes closes itself
   * (`Rejected{unauthorized}` → `ConnState.Closed`, never issuing `onServerMessage`):
   * clear the persisted record, reset the store, tear the dead session down, forget
   * the rejected token, and return to a fresh Lobby — never retry a token the server
   * has already rejected. */
  function handleStaleToken(): void {
    clearBudget();
    stopPolling();
    const key = sessionKeyRef.current;
    if (key !== null) {
      clearMatchSession(key);
    }
    sessionRef.current = null;
    sessionKeyRef.current = null;
    lodRef.current = null;
    credentialRef.current = null;
    reloadEntryPendingRef.current = false;
    setSeams(null);
    setStalled(false);
    useClientStore.getState().reset();
    // Forget the rejected token so nothing (a stray poll) can resurrect it —
    // `dropToLobbyMatchGone`'s precedent.
    lobbyClientRef.current = new LobbyClient({
      baseUrl: deps.lobby?.baseUrl ?? DEFAULT_LOBBY_BASE_URL,
      fetchImpl: deps.lobby?.fetchImpl,
    });
    setPhase('lobby'); // credentialRef is already null — persistSession() clears it
    navigateRef.current('/');
  }

  // --- the root-owned roster poll loop (design DD5) ----------------------------

  function schedulePoll(): void {
    if (pollTimerRef.current !== null) {
      return; // already scheduled
    }
    const phase = sessionPhaseRef.current;
    if (phase !== 'lobby' && phase !== 'connecting') {
      return;
    }
    const client = lobbyClientRef.current;
    if (client === null || client.token === null) {
      return; // no match to poll (the root drove no create/join)
    }
    pollTimerRef.current = schedulerRef.current!.setTimer(() => {
      pollTimerRef.current = null;
      runPoll();
    }, pollIntervalMs);
  }

  function stopPolling(): void {
    if (pollTimerRef.current !== null) {
      schedulerRef.current!.clearTimer(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }

  /** Re-arms the phase-appropriate budget/poll for the CURRENT phase (design DD9,
   * review fix F1). Called on every mount-effect setup so a StrictMode cleanup — which
   * clears both timers — is always followed by a setup that restores them; the guarded
   * one-time `rehydrateFromStore()` cannot, since its `rehydratedRef` short-circuits the
   * second invocation. Idempotent by construction: `armBudget` clears-then-sets and
   * `schedulePoll` no-ops when a poll is already scheduled, so a first setup that already
   * armed a timer and this re-arm together leave exactly one live timer of each kind (no
   * double-fire). A live user hand-off (`create`/`ready`/`start`/`retry`) arms its own
   * timers directly and does not route through here — this covers only the reload/mount
   * path and its StrictMode double-invoke. */
  function activatePhaseTimers(): void {
    const phase = sessionPhaseRef.current;
    if (phase === 'connecting') {
      armBudget();
      schedulePoll();
    } else if (phase === 'lobby') {
      schedulePoll();
    }
    // `in-match`/`finished`: no bounded budget and no roster poll.
  }

  function runPoll(): void {
    const phase = sessionPhaseRef.current;
    if (phase !== 'lobby' && phase !== 'connecting') {
      return;
    }
    const client = lobbyClientRef.current;
    if (client === null || client.token === null) {
      schedulePoll();
      return;
    }
    client.poll().then(
      (lobby) => {
        useClientStore.getState().setLobby(lobby);
        schedulePoll();
      },
      (err) => {
        // A `MatchNotFound` (404 — host cancelled/gone) drops back to the lobby from
        // either `lobby` or `connecting` (design DD5). Other errors are transient —
        // keep polling.
        if (err instanceof LobbyHttpError && err.status === 404) {
          dropToLobbyMatchGone();
        } else {
          schedulePoll();
        }
      },
    );
  }

  /** The poll `MatchNotFound` terminal (design DD5): the match is gone, so tear the
   * session down and return to a clean lobby (create/join) rather than polling a dead
   * match. */
  function dropToLobbyMatchGone(): void {
    clearBudget();
    stopPolling();
    const key = sessionKeyRef.current;
    if (key !== null) {
      clearMatchSession(key); // disconnects + forgets the registry entry
    }
    sessionRef.current = null;
    sessionKeyRef.current = null;
    lodRef.current = null;
    credentialRef.current = null;
    setStalled(false);
    setSeams(null);
    sessionPhaseRef.current = 'lobby';
    setSessionPhaseState('lobby');
    const store = useClientStore.getState();
    store.setLobby(null);
    store.setSession({ sessionToken: null, matchId: null, role: null, colonyId: null });
    // Forget the dead match's token so the poll loop cannot resurrect against it.
    lobbyClientRef.current = new LobbyClient({
      baseUrl: deps.lobby?.baseUrl ?? DEFAULT_LOBBY_BASE_URL,
      fetchImpl: deps.lobby?.fetchImpl,
    });
  }

  // --- the connecting hand-off (design DD5) ------------------------------------

  function beginConnecting(isHost: boolean): void {
    isHostRef.current = isHost;
    setStalled(false);
    setPhase('connecting');
    reconnectOrOpen();
    armBudget();
  }

  // --- store reset + prior-session teardown between matches (design DD10, D-079) -

  /** The store-reset + finished-session cleanup shared by every new-match
   * transition (`create`/`join`, which is also "play again with this lobby" —
   * the shipped surface has no separate route for it, Summary.tsx). `reset()`
   * runs *before* the new session installs (before `credentialRef` is set and
   * before `beginConnecting()` can call `setSession`/`getOrCreateMatchSession`),
   * so a second match never inherits the previous match's result, timeline,
   * manifest, provenance, or prompt-cycle count. A still-registered prior
   * session (a finished match's connection that outlived it, since the root is
   * never unmounted between matches) is torn down via `clearMatchSession`
   * rather than leaking a second live socket under a different key.
   * `expectingCloseRef` is set first so the prior session's own
   * `onStateChange(Closed)` — fired synchronously by `disconnect()` — is not
   * mistaken for a stale-token rejection (the same guard `runNonHostTerminal`/
   * `onBudgetExpired` use for their own deliberate disconnects); without it the
   * prior session's teardown would itself invoke `handleStaleToken()`, which
   * mints a fresh `LobbyClient` out from under the very `create`/`join` call
   * that is already in flight against the old one. */
  function resetForNewMatch(): void {
    clearBudget();
    stopPolling();
    const oldKey = sessionKeyRef.current;
    if (oldKey !== null) {
      expectingCloseRef.current = true;
      clearMatchSession(oldKey);
    }
    sessionRef.current = null;
    sessionKeyRef.current = null;
    lodRef.current = null;
    pendingConfirmTierRef.current = null;
    credentialRef.current = null;
    reloadEntryPendingRef.current = false;
    setSeams(null);
    setStalled(false);
    useClientStore.getState().reset();
  }

  // --- the presentational Lobby's hand-off entry points (design DD5) -----------

  async function create(name: string, config: LobbyConfig): Promise<void> {
    const client = lobbyClientRef.current!;
    const { session, lobby } = await client.create(name, config, deps.lobby?.opponent);
    resetForNewMatch();
    credentialRef.current = { token: session.token, matchId: session.matchId };
    isHostRef.current = true;
    useClientStore.getState().setLobby(lobby);
    schedulePoll();
  }

  async function join(matchCode: string, name: string): Promise<void> {
    const client = lobbyClientRef.current!;
    const { session, lobby } = await client.join(matchCode, name);
    resetForNewMatch();
    credentialRef.current = { token: session.token, matchId: session.matchId };
    isHostRef.current = false;
    useClientStore.getState().setLobby(lobby);
    schedulePoll();
  }

  async function claim(colonyId: number): Promise<void> {
    const client = lobbyClientRef.current!;
    const lobby = await client.claimColony(colonyId);
    useClientStore.getState().setLobby(lobby);
  }

  async function ready(readyFlag: boolean): Promise<void> {
    const client = lobbyClientRef.current!;
    const lobby = await client.ready(readyFlag);
    useClientStore.getState().setLobby(lobby);
    if (readyFlag) {
      beginConnecting(false);
    }
  }

  async function start(): Promise<void> {
    const client = lobbyClientRef.current!;
    await client.start();
    beginConnecting(true);
  }

  async function cancel(): Promise<void> {
    if (sessionPhaseRef.current !== 'connecting') {
      return;
    }
    runNonHostTerminal();
  }

  function retry(): void {
    if (sessionPhaseRef.current !== 'connecting') {
      return;
    }
    setStalled(false);
    reconnectOrOpen();
    armBudget();
  }

  // --- page-reload rehydrate (design DD8) ---------------------------------------

  /** Classifies a persisted `SessionStore` record on mount and resumes the right
   * phase (the D9 table): `lobby` rehydrates the `LobbyClient`'s token and polls,
   * opening no socket; `connecting` resumes the bounded, host-aware budget from the
   * persisted `isHost` (never a poll, W6-F1); `in-match`/`finished` reconnect over
   * `/ws`, and only `in-match` arms the reload-entry one-shot (`finished` reaches
   * `/summary` via the reactive `Result` edge instead). `role` is seeded into the
   * store for every reconnecting phase **before** `reconnectOrOpen()` — which itself
   * precedes `startSessionIfReady()`'s `bindColonyMessages(store)` bind — so the
   * monotonic never-re-promote guard (DD7) reads a live value on the very first
   * `Welcome` (W5-F1 seed-then-bind ordering) rather than only after the next
   * `MatchState`. A no-op if nothing was persisted (a fresh load starts `lobby`,
   * already the default state).
   *
   * Guarded by `rehydratedRef` (design DD9): this is the one mount-effect call that
   * can reach a live `connection.connect()` synchronously (via `reconnectOrOpen()`'s
   * `existing !== null` branch, once the first invocation already built the
   * session) — a `<StrictMode>` double-invoked mount effect must not run this a
   * second time or it would re-issue `connect()` on the already-connecting session
   * and churn the epoch. */
  function rehydrateFromStore(): void {
    if (rehydratedRef.current) {
      return;
    }
    rehydratedRef.current = true;
    const record = deps.sessionStore?.get() ?? null;
    if (record === null) {
      return;
    }
    credentialRef.current = { token: record.token, matchId: record.matchId };
    isHostRef.current = record.isHost;
    lobbyClientRef.current!.setSession({ token: record.token, matchId: record.matchId });

    switch (record.phase) {
      case 'lobby':
        schedulePoll();
        return;
      case 'connecting':
        useClientStore.getState().setSession({ role: record.role });
        setStalled(false);
        setPhase('connecting');
        reconnectOrOpen();
        armBudget();
        return;
      case 'in-match':
        reloadEntryPendingRef.current = true;
        useClientStore.getState().setSession({ role: record.role });
        setPhase('in-match');
        reconnectOrOpen();
        return;
      case 'finished':
        useClientStore.getState().setSession({ role: record.role });
        setPhase('finished');
        reconnectOrOpen();
        return;
    }
  }

  // The store-subscription handler, refreshed each render so it reads the latest
  // closures while the subscription registered once in the mount effect stays stable.
  onStoreChangeRef.current = (state, prev) => {
    startSessionIfReady();
    // The persist-on-change writer's store-field trigger (design DD8): a `phase`
    // change persists via `setPhase` itself (above); a session-field change — the
    // `welcome`/`applyMatchState` role reconciliation, `reconnectOrOpen`'s initial
    // `setSession`, or a full session teardown — persists here.
    if (
      state.sessionToken !== prev.sessionToken ||
      state.matchId !== prev.matchId ||
      state.role !== prev.role
    ) {
      persistSession();
    }
    // Entry advance, role-keyed and gated as a one-shot on either the live
    // `connecting → in-match` phase transition (design DD6/DD7, W3-F1) or the
    // reload-entry one-shot armed by an `in-match` rehydrate (design DD8, W4-F1) — a
    // reconnecting reload sets `sessionPhase` straight to `in-match` and never
    // crosses `connecting`, so the phase gate alone cannot fire for it. Because
    // `setManifest` fires on *every* `Welcome`, a transient socket drop that
    // re-delivers `Welcome` re-writes `manifest` (a new object → the subscription
    // re-fires); both gates are one-shots (the phase flips to `in-match` and
    // `reloadEntryPendingRef` disarms), so a `manifest` re-write observed once
    // already `in-match` — whichever gate got it there — is inert: a zoomed-out live
    // player is not yanked back to `/colony`. A not-yet-arrived `MatchState` is never
    // a demotion, so a `Welcome → keyframe` with no `MatchState` still lands a live
    // player on `/colony`.
    if (
      state.manifest !== null &&
      state.manifest !== prev.manifest &&
      (sessionPhaseRef.current === 'connecting' || reloadEntryPendingRef.current)
    ) {
      reloadEntryPendingRef.current = false;
      setStalled(false);
      setPhase('in-match');
      navigateRef.current(state.role === 'spectator' ? '/match' : '/colony');
    }
    // Elimination demotion (design DD6(b)/DD7): the own colony entering
    // `MatchState.eliminations` (the `selectOwnEliminated` false→true transition) demotes
    // a player to the Match spectator view. Stays reactive — it is a genuine role
    // transition, not the entry advance.
    if (!selectOwnEliminated(prev) && selectOwnEliminated(state)) {
      navigateRef.current('/match');
    }
    // Result demotion (design DD6(c)): the match ending (`matchResult` observed for
    // the first time this session) navigates to the Summary. Reactive and independent
    // of phase/role, so a `finished`-phase reload — which arms no reload-entry
    // one-shot — reaches `/summary` through this edge alone (design DD8).
    if (prev.matchResult === null && state.matchResult !== null) {
      navigateRef.current('/summary');
    }
  };

  useEffect(() => {
    // A page-reload rehydrate (design DD8) classifies any persisted session first —
    // it may itself install the store session fields (`reconnectOrOpen`), which
    // `startSessionIfReady` then reads to build+connect the session. Both are one-time
    // work: `rehydrateFromStore` is guarded by `rehydratedRef` and `startSessionIfReady`
    // by the session/registry check, so a StrictMode double-invoke (setup → cleanup →
    // setup) never re-classifies or re-`connect()`s.
    rehydrateFromStore();
    startSessionIfReady();
    // …but the phase-appropriate budget/poll timers are *repeatable* activation, not
    // one-time work: the cleanup below clears both on the synthetic StrictMode unmount,
    // and the guarded rehydrate cannot re-arm them on the surviving second setup. Re-arm
    // them here on EVERY setup — idempotently (`armBudget` clears-then-sets, `schedulePoll`
    // no-ops when already scheduled), so the surviving mount ends with exactly one live
    // timer of each kind and a `connecting`/`lobby` reload keeps a working budget/poll
    // through a double mount (design DD9, review fix F1).
    activatePhaseTimers();
    const unsubscribe = useClientStore.subscribe((state, prev) => onStoreChangeRef.current(state, prev));
    return () => {
      unsubscribe();
      // Timers are re-armed on remount (see `activatePhaseTimers` above); the connection
      // is deliberately NOT disconnected on a transient unmount (design DD9).
      clearBudget();
      stopPolling();
    };
    // `deps` is a stable DI bundle injected once by the caller, not reactive props;
    // the root owns one connection for its lifetime and does not rebuild it per render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const lobbyController: SessionLobbyController = {
    phase: sessionPhase,
    connectingStalled,
    create,
    join,
    claim,
    ready,
    start,
    cancel,
    retry,
  };

  // The Colony-route underlay seams (design DD7/DD8), derived from the one session + the
  // reactive `ownHive`: undefined until a session is live AND `ownHive` is present, so the
  // Colony screen stays chrome-only over a placeholder until then. The seam carries the raw
  // `ownHive` anchor, not a precomputed camera — the underlay derives the hive `Camera` and its
  // matching Close viewport from the MEASURED container (design DD9 / implementation-review F2),
  // which only it can measure once mounted.
  const colonyUnderlay: ColonyUnderlaySeams | undefined =
    seams !== null && ownHive !== null
      ? {
          sessionKey: seams.sessionKey,
          createRenderer: seams.createRenderer,
          ownHive,
          lod: seams.colonyLod,
        }
      : undefined;

  return (
    <>
      {upgradeRequiredMinVersion !== null && (
        <div
          data-testid="forced-refresh-notice"
          role="alert"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 16,
            padding: 24,
            textAlign: 'center',
            background: 'var(--surface-app, #1a1206)',
            color: 'var(--ink-1, #fff)',
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 18 }}>Update required</div>
          <div style={{ maxWidth: 360, fontSize: 14 }}>
            This match needs a newer version of the client (v{upgradeRequiredMinVersion}). Refresh
            the page to load the latest and rejoin.
          </div>
          <button
            data-testid="forced-refresh-reload"
            onClick={() => window.location.reload()}
            style={{
              minHeight: 44,
              padding: '0 20px',
              borderRadius: 999,
              border: 'none',
              background: 'var(--accent-strong, #e0a400)',
              color: 'var(--ink-1, #1a1206)',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            Refresh now
          </button>
        </div>
      )}
      <AppRoutes
        lobbyController={lobbyController}
        colonyController={seams?.colonyController}
        colonyUnderlay={colonyUnderlay}
        colonySessionPhase={sessionPhase}
        colonyPvpChat={pvpChat}
        matchOverlay={
          deps.pvp === true && liveToken !== null
            ? <PvpPromptFeed mode="player" token={liveToken} baseUrl={deps.lobby?.baseUrl} fetchImpl={deps.lobby?.fetchImpl} />
            : undefined
        }
        // The two-colony Match HUD's own reads (`match-hud`), over the same live bearer the
        // prompt overlay above already carries — unless the composition supplied its own seams
        // (`pvp-ui-fixes` W3), which only the presentation projector does.
        matchHud={resolveMatchHudSeams({
          explicit: deps.matchHud,
          pvp: deps.pvp,
          liveToken,
          baseUrl: deps.lobby?.baseUrl,
          fetchImpl: deps.lobby?.fetchImpl,
        })}
        inMatchDeps={seams?.inMatchDeps}
        lod={seams?.lod}
        cameraLod={seams?.cameraLod}
        createRenderer={seams?.createRenderer}
        sound={deps.sound}
        summaryPrimaryAction={deps.presentationRoundsHandoff ? { label: 'Play Rounds', path: '/', external: true } : undefined}
      />
    </>
  );
}
